import { FileService } from './file-service.js';
import { IssueService } from './issue-service.js';
import { IntentService } from './intent-service.js';
import { PathnameService } from './pathname-service.js';
import { ResolvedConfig } from '../utils/config.js';
import path from 'path';

export interface SyncChange {
  id: string;
  from: string;
  to: string;
  reason: string;
}

export interface SyncError {
  id: string;
  error: string;
}

export interface SyncResult {
  changes: SyncChange[];
  errors?: SyncError[];
}

/**
 * Service for synchronizing file locations with their metadata.
 * This service is responsible for:
 * - Finding mismatched file locations
 * - Generating sync plans
 * - Executing file moves safely
 */
export class SyncService {
  constructor(
    private fileService: FileService,
    private issueService: IssueService,
    private intentService: IntentService,
    private pathnameService: PathnameService,
    private config: ResolvedConfig
  ) {}

  /**
   * Analyze the current state and generate a sync plan without making changes.
   */
  async analyzeSyncNeeded(): Promise<SyncResult> {
    const changes: SyncChange[] = [];
    const errors: SyncError[] = [];

    // Get all markdown files in all possible directories
    const allDirs = this.pathnameService.getActivityDirectories();
    const files = await this.fileService.findFiles(
      (f) => f.endsWith('.md'),
      allDirs
    );

    // Check each file
    for (const file of files) {
      try {
        // Get current metadata
        const metadata = await this.issueService.frontmatterMethods.getFrontmatterMetadata(file);
        if (!metadata) {
          errors.push({
            id: path.basename(file),
            error: 'No frontmatter found'
          });
          continue;
        }

        // Let intent service determine where it should be
        const intent = this.intentService.interpretIntent({
          pathname: file,
          metadata
        });

        // Get the target path
        const targetPath = path.join(
          intent.targetLocation.dirname,
          intent.targetLocation.basename
        );

        // If paths don't match, we need to move the file
        if (path.normalize(file) !== path.normalize(targetPath)) {
          changes.push({
            id: metadata.id || path.basename(file),
            from: file,
            to: targetPath,
            reason: intent.reason
          });
        }

        // If metadata needs updating, add that as a change too
        if (JSON.stringify(metadata) !== JSON.stringify(intent.targetMetadata)) {
          changes.push({
            id: metadata.id || path.basename(file),
            from: file,
            to: file, // Same file, just metadata update
            reason: 'Metadata needs to be updated to match intent'
          });
        }
      } catch (error: any) {
        errors.push({
          id: path.basename(file),
          error: error.message
        });
      }
    }

    return {
      changes,
      errors: errors.length > 0 ? errors : undefined
    };
  }

  /**
   * Execute the sync plan, moving files to their correct locations.
   */
  async executeSync(dryRun: boolean = false): Promise<SyncResult> {
    // First analyze what needs to be done
    const plan = await this.analyzeSyncNeeded();

    // If this is a dry run, just return the plan
    if (dryRun) {
      return plan;
    }

    const errors: SyncError[] = plan.errors || [];
    const executedChanges: SyncChange[] = [];

    // Execute each change
    for (const change of plan.changes) {
      try {
        // Skip if we've already processed this file
        if (executedChanges.some(ec => ec.id === change.id)) {
          continue;
        }

        // Get current metadata
        const metadata = await this.issueService.frontmatterMethods.getFrontmatterMetadata(change.from);
        if (!metadata) {
          throw new Error('No frontmatter found');
        }

        // Let intent service determine final state
        const intent = this.intentService.interpretIntent({
          pathname: change.from,
          metadata
        });

        // Move file with updated metadata
        await this.issueService.fileMethods.moveFileWithMetadata(
          change.from,
          change.to,
          intent.targetMetadata
        );

        executedChanges.push(change);
      } catch (error: any) {
        errors.push({
          id: change.id,
          error: error.message
        });
      }
    }

    return {
      changes: executedChanges,
      errors: errors.length > 0 ? errors : undefined
    };
  }
} 